// Rename + delete state for a board (#1033) — the confirm/dialog half of the
// dashboards writes, split from the two routes that need it (the index and the
// board view) the same way `createTokenRevoke` is: a mutation with a confirm, a
// re-entrancy guard and a failure notice is state, not rendering. Per-surface,
// never a global store.
//
// Built-ins are refused server-side (409). Neither surface OFFERS these actions
// on a built-in row, so a 409 here is belt-and-braces — which is exactly why it
// must stay legible: the API's `detail` is shown, never a thrown view.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from './api';
import { deleteDashboard, renameDashboard } from './dashboard-writes';

/** The least a rename/delete needs to name its target — a `DashboardSummary`
 * (index rows) and a `DashboardDetail` (the board view) both satisfy it. */
export type DashboardTarget = { readonly id: string; readonly name: string };

/** Which action a failure belongs to — a rename renders inside its own dialog, a
 * delete in the surface's toast (a `<ConfirmDialog/>` has no inline slot). */
type Failure = { readonly of: 'rename' | 'delete'; readonly message: string };

export type DashboardActions = {
  /** The board awaiting a new name, or null. Feed to `<DashboardRenameModal/>`. */
  readonly renameTarget: () => DashboardTarget | null;
  /** The board awaiting delete confirmation, or null. Feed to `<ConfirmDialog/>`. */
  readonly deleteTarget: () => DashboardTarget | null;
  /** True while either write is in flight — disables the submit. */
  readonly pending: () => boolean;
  readonly renameError: () => string | null;
  readonly deleteError: () => string | null;
  readonly askRename: (board: DashboardTarget) => void;
  readonly askDelete: (board: DashboardTarget) => void;
  /** Dismiss whichever dialog is open, without writing. */
  readonly close: () => void;
  /** Clear the failure notice — wire to the `<Toast/>`'s `onDismiss`. */
  readonly clearError: () => void;
  readonly submitRename: (name: string) => void;
  readonly confirmDelete: () => void;
};

/**
 * `onRenamed` / `onDeleted` fire once the API has ACCEPTED the write; the caller
 * re-reads (or navigates) from there. Neither is handed the response body on
 * purpose — what the account looks like after a write is a server read, not an echo.
 *
 * A failure leaves the dialog open with the reason set: a delete that quietly
 * no-ops would leave a maintainer believing a shared board is gone.
 */
export function createDashboardActions(input: {
  readonly token: string | null;
  readonly onRenamed: () => void;
  readonly onDeleted: (board: DashboardTarget) => void;
}): DashboardActions {
  const [renameTarget, setRenameTarget] = createSignal<DashboardTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = createSignal<DashboardTarget | null>(null);
  const [failure, setFailure] = createSignal<Failure | null>(null);
  const [pending, setPending] = createSignal(false);

  const errorFor = (of: Failure['of']) => (): string | null => {
    const f = failure();
    return f?.of === of ? f.message : null;
  };

  const close = (): void => {
    setRenameTarget(null);
    setDeleteTarget(null);
  };

  /** Drive one write: busy while it flies, `ok` on acceptance, the API's own
   * reason on refusal (409 built-in, 403 personal-scope, …). */
  const drive = (of: Failure['of'], write: Promise<unknown>, ok: () => void): void => {
    setPending(true);
    setFailure(null);
    void write
      .then(ok)
      .catch((e: unknown) =>
        setFailure({ of, message: errorReason(e, t(`dashboards.${of}_failed`)) }),
      )
      .finally(() => setPending(false));
  };

  return {
    renameTarget,
    deleteTarget,
    pending,
    renameError: errorFor('rename'),
    deleteError: errorFor('delete'),
    askRename: (board) => {
      close();
      setFailure(null);
      setRenameTarget(board);
    },
    askDelete: (board) => {
      close();
      setFailure(null);
      setDeleteTarget(board);
    },
    close,
    clearError: () => setFailure(null),
    submitRename: (name) => {
      const row = renameTarget();
      if (row === null || pending() || name.trim() === '') return;
      drive('rename', renameDashboard(row.id, name, input.token), () => {
        // Close only the dialog THIS call opened: nothing stops the reader
        // dismissing it and renaming another board mid-flight, and a blind clear
        // would dismiss that newer dialog out from under them.
        if (renameTarget()?.id === row.id) setRenameTarget(null);
        input.onRenamed();
      });
    },
    confirmDelete: () => {
      const row = deleteTarget();
      if (row === null || pending()) return;
      drive('delete', deleteDashboard(row.id, input.token), () => {
        if (deleteTarget()?.id === row.id) setDeleteTarget(null);
        input.onDeleted(row);
      });
    },
  };
}
